/**
 * МОК-СЕРВЕР — эмуляция бэкенда по контракту из ТЗ (п.3, п.4).
 *
 *   GET /api/health/meta
 *     -> { default_type, periods: { year: [...], quarter: [...] } }
 *
 *   GET /api/health/dashboard?type&report&from&to
 *     -> { summary: { total, good, bad, no_data, completion_pct },
 *          items: [ { code, sort_order, name_ru, name_kz, unit_ru, unit_kz,
 *                     direction, plan, fact, status, series: [...] } ] }
 *
 * Статусы (good/bad/no_data) и summary считает «бэкенд» (этот файл) —
 * фронтенд их только отображает (ТЗ п.6, критерий приёмки 4).
 *
 * Демо пограничных случаев (ТЗ п.8) встроено в данные:
 *   - satisfaction: no_data за 2025-Q2 (fact=null) — разрыв линии, плашка «Нет данных»;
 *   - ambulance_20min: пропущен период 2024-Q2 — ряд идёт по существующим точкам;
 *   - maternal_mortality: plan=0 — стрелка «→» при fact=0.
 */

/* ---------- Справочник периодов ---------- */

const YEARS = [2023, 2024, 2025];
const QUARTERS = [
  [2024, 1],
  [2024, 2],
  [2024, 3],
  [2024, 4],
  [2025, 1],
  [2025, 2],
];

const KZ_ROMAN = ["I", "II", "III", "IV"];

const yearPeriod = (y) => ({
  key: String(y),
  type: "year",
  year: y,
  quarter: null,
  label_ru: `${y} — год`,
  label_kz: `${y} — жыл`,
});

const quarterPeriod = ([y, q]) => ({
  key: `${y}-Q${q}`,
  type: "quarter",
  year: y,
  quarter: q,
  label_ru: `${KZ_ROMAN[q - 1]} квартал ${y}`,
  label_kz: `${y} ж. ${KZ_ROMAN[q - 1]} тоқсан`,
});

const PERIODS = {
  year: YEARS.map(yearPeriod),
  quarter: QUARTERS.map(quarterPeriod),
};

/* ---------- Справочник показателей ----------
   values: key периода -> fact (null = нет данных; отсутствие ключа = периода нет вовсе)
   plans:  key периода -> plan (для кварталов — годовой план, ТЗ п.9.9)          */

const INDICATORS = [
  {
    code: "suicide_rate",
    sort_order: 1,
    direction: "low",
    name_ru: "Показатель суицидов",
    name_kz: "Суицид көрсеткіші",
    unit_ru: "на 100 тыс.",
    unit_kz: "100 мыңға",
    plans: { 2023: 7.4, 2024: 7.4, 2025: 7.4 },
    values: {
      2023: 5.1,
      2024: 7.0,
      2025: 1.19,
      "2024-Q1": 6.8,
      "2024-Q2": 7.2,
      "2024-Q3": 6.4,
      "2024-Q4": 7.0,
      "2025-Q1": 2.3,
      "2025-Q2": 1.19,
    },
  },
  {
    code: "maternal_mortality",
    sort_order: 2,
    direction: "low",
    name_ru: "Материнская смертность",
    name_kz: "Ана өлімі",
    unit_ru: "случаев",
    unit_kz: "жағдай",
    plans: { 2023: 0, 2024: 0, 2025: 0 },
    values: {
      2023: 0,
      2024: 0,
      2025: 0,
      "2024-Q1": 0,
      "2024-Q2": 0,
      "2024-Q3": 0,
      "2024-Q4": 0,
      "2025-Q1": 0,
      "2025-Q2": 0,
    },
  },
  {
    code: "infant_mortality",
    sort_order: 3,
    direction: "low",
    name_ru: "Младенческая смертность",
    name_kz: "Нәресте өлімі",
    unit_ru: "на 1000",
    unit_kz: "1000-ға",
    plans: { 2023: 9.5, 2024: 9.5, 2025: 9.5 },
    values: {
      2023: 7.3,
      2024: 8.8,
      2025: 4.9,
      "2024-Q1": 9.1,
      "2024-Q2": 8.5,
      "2024-Q3": 8.9,
      "2024-Q4": 8.8,
      "2025-Q1": 5.6,
      "2025-Q2": 4.9,
    },
  },
  {
    code: "neonatal_mortality",
    sort_order: 4,
    direction: "low",
    name_ru: "Неонатальная смертность",
    name_kz: "Неонаталдық өлім",
    unit_ru: "на 1000",
    unit_kz: "1000-ға",
    plans: { 2023: 3.85, 2024: 3.85, 2025: 3.85 },
    values: {
      2023: 4.3,
      2024: 3.6,
      2025: 3.7,
      "2024-Q1": 3.9,
      "2024-Q2": 3.7,
      "2024-Q3": 3.5,
      "2024-Q4": 3.6,
      "2025-Q1": 3.8,
      "2025-Q2": 3.7,
    },
  },
  {
    code: "child_mortality",
    sort_order: 5,
    direction: "low",
    name_ru: "Детская смертность",
    name_kz: "Балалар өлімі",
    unit_ru: "на 1000",
    unit_kz: "1000-ға",
    plans: { 2023: 11.4, 2024: 11.4, 2025: 11.4 },
    values: {
      2023: 12.1,
      2024: 10.8,
      2025: 4.9,
      "2024-Q1": 11.6,
      "2024-Q2": 11.0,
      "2024-Q3": 10.5,
      "2024-Q4": 10.8,
      "2025-Q1": 6.2,
      "2025-Q2": 4.9,
    },
  },
  {
    code: "standardized_mortality",
    sort_order: 6,
    direction: "low",
    name_ru: "Стандартизованная смертность",
    name_kz: "Стандартталған өлім",
    unit_ru: "на 1000",
    unit_kz: "1000-ға",
    plans: { 2023: 4.55, 2024: 4.55, 2025: 4.55 },
    values: {
      2023: 4.4,
      2024: 3.9,
      2025: 4.0,
      "2024-Q1": 4.2,
      "2024-Q2": 4.0,
      "2024-Q3": 3.8,
      "2024-Q4": 3.9,
      "2025-Q1": 4.1,
      "2025-Q2": 4.0,
    },
  },
  {
    code: "circulatory_mortality",
    sort_order: 7,
    direction: "low",
    name_ru: "Смертность от болезней системы кровообращения",
    name_kz: "Қан айналымы жүйесі ауруларынан болатын өлім",
    unit_ru: "на 100 тыс.",
    unit_kz: "100 мыңға",
    plans: { 2023: 132, 2024: 130, 2025: 128 },
    values: {
      2023: 141,
      2024: 127,
      2025: 119,
      "2024-Q1": 134,
      "2024-Q2": 129,
      "2024-Q3": 125,
      "2024-Q4": 127,
      "2025-Q1": 122,
      "2025-Q2": 119,
    },
  },
  {
    code: "cancer_mortality",
    sort_order: 8,
    direction: "low",
    name_ru: "Смертность от злокачественных новообразований",
    name_kz: "Қатерлі ісіктерден болатын өлім",
    unit_ru: "на 100 тыс.",
    unit_kz: "100 мыңға",
    plans: { 2023: 74.5, 2024: 73.0, 2025: 71.5 },
    values: {
      2023: 76.2,
      2024: 71.4,
      2025: 74.8,
      "2024-Q1": 74.1,
      "2024-Q2": 72.0,
      "2024-Q3": 70.9,
      "2024-Q4": 71.4,
      "2025-Q1": 73.2,
      "2025-Q2": 74.8,
    },
  },
  {
    code: "tb_incidence",
    sort_order: 9,
    direction: "low",
    name_ru: "Заболеваемость туберкулёзом",
    name_kz: "Туберкулезбен сырқаттанушылық",
    unit_ru: "на 100 тыс.",
    unit_kz: "100 мыңға",
    plans: { 2023: 46, 2024: 44, 2025: 42 },
    values: {
      2023: 44.2,
      2024: 42.8,
      2025: 38.5,
      "2024-Q1": 43.9,
      "2024-Q2": 43.1,
      "2024-Q3": 42.5,
      "2024-Q4": 42.8,
      "2025-Q1": 40.1,
      "2025-Q2": 38.5,
    },
  },
  {
    code: "screening_coverage",
    sort_order: 10,
    direction: "high",
    name_ru: "Охват скрининговыми осмотрами целевых групп",
    name_kz: "Нысаналы топтарды скринингтік тексерумен қамту",
    unit_ru: "%",
    unit_kz: "%",
    plans: { 2023: 95, 2024: 95, 2025: 97 },
    values: {
      2023: 93.4,
      2024: 96.1,
      2025: 97.8,
      "2024-Q1": 94.2,
      "2024-Q2": 95.5,
      "2024-Q3": 95.9,
      "2024-Q4": 96.1,
      "2025-Q1": 97.2,
      "2025-Q2": 97.8,
    },
  },
  {
    code: "doctors_provision",
    sort_order: 11,
    direction: "high",
    name_ru: "Обеспеченность врачами",
    name_kz: "Дәрігерлермен қамтамасыз етілу",
    unit_ru: "на 10 тыс.",
    unit_kz: "10 мыңға",
    plans: { 2023: 21.0, 2024: 21.5, 2025: 22.0 },
    values: {
      2023: 20.4,
      2024: 21.7,
      2025: 22.3,
      "2024-Q1": 21.1,
      "2024-Q2": 21.4,
      "2024-Q3": 21.6,
      "2024-Q4": 21.7,
      "2025-Q1": 22.1,
      "2025-Q2": 22.3,
    },
  },
  {
    code: "staffing_level",
    sort_order: 12,
    direction: "high",
    name_ru: "Укомплектованность врачебными кадрами",
    name_kz: "Дәрігер кадрлармен жасақталуы",
    unit_ru: "%",
    unit_kz: "%",
    plans: { 2023: 92, 2024: 93, 2025: 94 },
    values: {
      2023: 90.1,
      2024: 93.6,
      2025: 95.2,
      "2024-Q1": 92.3,
      "2024-Q2": 93.0,
      "2024-Q3": 93.4,
      "2024-Q4": 93.6,
      "2025-Q1": 94.7,
      "2025-Q2": 95.2,
    },
  },
  {
    // Демо пропущенного периода: нет 2024-Q2 (ТЗ п.8)
    code: "ambulance_20min",
    sort_order: 13,
    direction: "high",
    name_ru: "Доля вызовов скорой помощи со временем доезда до 20 минут",
    name_kz: "Жедел жәрдемнің 20 минутқа дейін жету үлесі",
    unit_ru: "%",
    unit_kz: "%",
    plans: { 2023: 90, 2024: 90, 2025: 92 },
    values: {
      2023: 88.7,
      2024: 91.2,
      2025: 93.5,
      "2024-Q1": 89.4,
      "2024-Q3": 91.0,
      "2024-Q4": 91.2,
      "2025-Q1": 92.8,
      "2025-Q2": 93.5,
    },
  },
  {
    // Демо no_data: fact=null за 2025 и 2025-Q2 (ТЗ п.8)
    code: "satisfaction",
    sort_order: 14,
    direction: "high",
    name_ru: "Удовлетворённость населения качеством медицинских услуг",
    name_kz: "Халықтың медициналық қызмет сапасына қанағаттануы",
    unit_ru: "%",
    unit_kz: "%",
    plans: { 2023: 95, 2024: 95, 2025: 97 },
    values: {
      2023: 93.4,
      2024: 96.1,
      2025: 97.8,
      "2024-Q1": 94.2,
      "2024-Q2": 95.5,
      "2024-Q3": 95.9,
      "2024-Q4": 96.1,
      "2025-Q1": 97.2,
      "2025-Q2": 97.8,
    },
  },
];

/* ---------- «Серверная» логика ---------- */

const computeStatus = (fact, plan, direction) => {
  if (
    fact === null ||
    fact === undefined ||
    plan === null ||
    plan === undefined
  )
    return "no_data";
  return (direction === "low" ? fact <= plan : fact >= plan) ? "good" : "bad";
};

const planForPeriod = (ind, periodObj) =>
  // для квартала — годовой план соответствующего года (ТЗ п.9.9)
  ind.plans[String(periodObj.year)] !== undefined
    ? ind.plans[String(periodObj.year)]
    : null;

const sliceForPeriod = (ind, periodObj) => {
  const has = Object.prototype.hasOwnProperty.call(ind.values, periodObj.key);
  const fact = has ? ind.values[periodObj.key] : null;
  const plan = planForPeriod(ind, periodObj);
  return { plan, fact, status: computeStatus(fact, plan, ind.direction) };
};

export const getMeta = () => ({
  default_type: "year",
  periods: PERIODS,
});

export const getDashboard = ({ type, report, from, to }) => {
  const periods = PERIODS[type] || [];
  const fromIdx = periods.findIndex((p) => p.key === from);
  const toIdx = periods.findIndex((p) => p.key === to);
  const reportObj = periods.find((p) => p.key === report);

  const rangePeriods =
    fromIdx >= 0 && toIdx >= 0 ? periods.slice(fromIdx, toIdx + 1) : [];

  const items = INDICATORS.map((ind) => {
    const slice = reportObj
      ? sliceForPeriod(ind, reportObj)
      : { plan: null, fact: null, status: "no_data" };

    // Ряд только по СУЩЕСТВУЮЩИМ периодам показателя (ТЗ п.8:
    // пропущенный период не заполняется искусственно)
    const series = rangePeriods
      .filter((p) => Object.prototype.hasOwnProperty.call(ind.values, p.key))
      .map((p) => {
        const s = sliceForPeriod(ind, p);
        return {
          key: p.key,
          type: p.type,
          year: p.year,
          quarter: p.quarter,
          label_ru: p.label_ru,
          label_kz: p.label_kz,
          plan: s.plan,
          fact: s.fact,
          status: s.status,
        };
      });

    return {
      code: ind.code,
      sort_order: ind.sort_order,
      name_ru: ind.name_ru,
      name_kz: ind.name_kz,
      unit_ru: ind.unit_ru,
      unit_kz: ind.unit_kz,
      direction: ind.direction,
      plan: slice.plan,
      fact: slice.fact,
      status: slice.status,
      series,
    };
  }).sort((a, b) => a.sort_order - b.sort_order);

  const good = items.filter((i) => i.status === "good").length;
  const bad = items.filter((i) => i.status === "bad").length;
  const no_data = items.filter((i) => i.status === "no_data").length;
  const total = items.length;

  return {
    summary: {
      total,
      good,
      bad,
      no_data,
      // знаменатель — полный перечень показателей (ТЗ п.5.4)
      completion_pct: total ? Math.round((good / total) * 100) : 0,
    },
    items,
  };
};
